import { is } from "@callcenter/helpers"
import { combine, createEffect, createEvent, createStore, sample } from "effector"
import { rxSample } from "rx-sample"
import type { CallcenterBalanceChannelApi } from "@callcenter/api"
import { callcenterSocket } from "@callcenter/api"
import { $callcenterService } from "@callcenter/effector-services"
import { createGate } from "@callcenter/effector-helpers"

import {
  TRIAL_BALANCE_BLOCK_LIMIT_USD,
  TRIAL_BALANCE_SOFT_LIMIT_USD,
  isTrialBalanceLowDismissedThisSession,
  markTrialBalanceLowDismissed
} from "../shared/trial-balance"
import { $isActiveTrial, $isSelfServiceMode } from "./self-service.model"

export const { PageGate: BalanceTrialGate, pageOpened, pageClosed } = createGate()

export const balanceLowDismissed = createEvent()

// Backend (VN-17246) extends CallcenterBalanceChannelStatus with "low_balance".
// While the @callcenter/api type still lists only "success" | "payment_failure"
// we treat the channel payload as a permissive shape at the read site.
type IncomingBalanceUpdate = CallcenterBalanceChannelApi.CallcenterBalanceChannelStreamData

const balanceChannelReceived = createEvent<IncomingBalanceUpdate>()

const parseBalance = (raw: string | null): number | null => {
  if (raw === null) {
    return null
  }
  const parsed = Number.parseFloat(raw)

  return is.finite(parsed) ? parsed : null
}

const $balanceFromSettings = $callcenterService.$settings.map((settings) =>
  is.number(settings.self_service_trial_balance) ? settings.self_service_trial_balance : null
)

// Channel-derived store keeps the most recent cable update; null until the
// first low_balance/payment update arrives. combine below derives the
// effective balance from both sources so the value seeded into
// $callcenterService.$settings on cold load (me.json) is honoured even when
// the cable channel has not pushed yet.
const $balanceFromChannel = createStore<number | null>(null).on(balanceChannelReceived, (_, { balance }) =>
  parseBalance(balance)
)

export const $trialBalance = combine(
  $balanceFromSettings,
  $balanceFromChannel,
  (fromSettings, fromChannel) => fromChannel ?? fromSettings
)

rxSample({
  source: callcenterSocket.CallcenterBalanceChannel.onUpdate,
  subscribeOn: pageOpened,
  unsubscribeOn: pageClosed,
  target: balanceChannelReceived
})

// Reads on $isSelfServiceMode rather than $isActiveTrial: once BE marks the
// trial as expired (after balance hits 0), $isActiveTrial flips to false and
// the overlay would disappear. The BE does not expose an expiry reason, so we
// keep the balance overlay visible whenever we observe balance ≤ 0 on a
// self-service callcenter — that is the FE signal for "ended due to balance".
export const $isTrialBalanceBlocked = combine(
  $isSelfServiceMode,
  $trialBalance,
  (isSelfServiceMode, balance) => isSelfServiceMode && balance !== null && balance <= TRIAL_BALANCE_BLOCK_LIMIT_USD
)

const $isTrialBalanceLowRaw = combine(
  $isActiveTrial,
  $trialBalance,
  (active, balance) =>
    active && balance !== null && balance <= TRIAL_BALANCE_SOFT_LIMIT_USD && balance > TRIAL_BALANCE_BLOCK_LIMIT_USD
)

const persistDismissFx = createEffect(() => markTrialBalanceLowDismissed())

export const $balanceLowDismissed = createStore<boolean>(isTrialBalanceLowDismissedThisSession()).on(
  balanceLowDismissed,
  () => true
)

sample({ clock: balanceLowDismissed, target: persistDismissFx })

export const $shouldShowTrialBalanceLow = combine(
  $isTrialBalanceLowRaw,
  $isTrialBalanceBlocked,
  $balanceLowDismissed,
  (low, blocked, dismissed) => low && !blocked && !dismissed
)

export const $hasTrialBalanceWarning = combine(
  $shouldShowTrialBalanceLow,
  $isTrialBalanceBlocked,
  (low, blocked) => low || blocked
)
